// src/components/InternalOverviewView.js
import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useNavigate, useParams } from 'react-router-dom';
import { departmentQueries } from '../repository/graphQueries';
import { formatDate, formatDateTime, formatScore } from '../utils';
import EisenhowerMatrixView from './EisenhowerMatrixView';
// Import the icon for the tooltip
import { RiInformationLine as InformationCircleIcon } from 'react-icons/ri';
import { DRI_EXPLANATION } from '../constants'; // Import the DRI explanation

const DropdownCheckbox = ({ options, selectedOptions, onChange, title }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div className="relative inline-block text-left">
            <div>
                <button
                    type="button"
                    className="inline-flex justify-center w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none"
                    onClick={() => setIsOpen(!isOpen)}
                >
                    {title}
                    <svg className="-mr-1 ml-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                </button>
            </div>

            {isOpen && (
                <div className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-20">
                    <div className="py-1" role="menu" aria-orientation="vertical">
                        {options.map(option => (
                            <label key={option} className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                                <input
                                    type="checkbox"
                                    className="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
                                    checked={selectedOptions.includes(option)}
                                    onChange={() => onChange(option)}
                                />
                                <span className="ml-3">{option}</span>
                            </label>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};


// Tooltip component (can be a shared component or defined locally if not already shared)
const Tooltip = ({ content, children }) => {
    const [isVisible, setIsVisible] = useState(false);
    
    return (
        <div className="relative inline-block">
            <div
                className="inline-flex items-center cursor-help"
                onMouseEnter={() => setIsVisible(true)}
                onMouseLeave={() => setIsVisible(false)}
                onClick={() => setIsVisible(!isVisible)} // For mobile support
            >
                {children}
            </div>
            {isVisible && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-72 p-3 text-sm bg-gray-800 text-white rounded-md shadow-lg z-50">
                    {content}
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 w-0 h-0 border-x-4 border-x-transparent border-b-4 border-b-gray-800"></div>
                </div>
            )}
        </div>
    );
};


const InternalOverviewView = () => {
    const { neo4jService, departmentId: departmentIdFromContext, dateRange } = useOutletContext();
    const navigate = useNavigate();
    const params = useParams();
    
    const activeRouteDepartmentId = departmentIdFromContext || params.departmentId;

    const [departments, setDepartments] = useState([]);
    const [selectedDepartment, setSelectedDepartment] = useState(activeRouteDepartmentId || '');
    
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    const [priorityCounts, setPriorityCounts] = useState({
        criticalIssues: 0,
        totalIssues: 0,
        blockedInitiatives: 0,
        totalInitiatives: 0,
        overdueActions: 0,
        neglectedGoals: 0,
        totalGoals: 0
    });
    
    const [staffWorkload, setStaffWorkload] = useState([]);
    const [clientHealth, setClientHealth] = useState([]);
    const [recentComms, setRecentComms] = useState([]);

    const [dris, setDris] = useState([]);
    const [loadingDris, setLoadingDris] = useState(true);
    const [selectedDris, setSelectedDris] = useState([]);

    const driTooltipContent = DRI_EXPLANATION;

    const loadDris = useCallback(async () => {
        if (!neo4jService || !selectedDepartment) return;
        setLoadingDris(true);
        try {
            const driResult = await neo4jService.runQuery(departmentQueries.getDepartmentDRIs, { departmentId: selectedDepartment });
            setDris(driResult.map(r => r.dri));
        } catch (err) {
            console.error('Error loading DRIs:', err);
        } finally {
            setLoadingDris(false);
        }
    }, [neo4jService, selectedDepartment]);

    useEffect(() => {
        if (selectedDepartment) {
            loadDris();
        }
    }, [selectedDepartment, loadDris]);

    const handleDriChange = (dri) => {
        setSelectedDris(prev =>
            prev.includes(dri) ? prev.filter(d => d !== dri) : [...prev, dri]
        );
    };

    useEffect(() => {
        if (activeRouteDepartmentId && activeRouteDepartmentId !== selectedDepartment) {
            setSelectedDepartment(activeRouteDepartmentId);
        }
    }, [activeRouteDepartmentId, selectedDepartment]);

    const loadDepartments = useCallback(async () => {
        if (!neo4jService) return;
        try {
            const deptResult = await neo4jService.runQuery(departmentQueries.listAllDepartments);
            const deptList = deptResult.map(r => r.department).filter(d => d);
            setDepartments(deptList);
            
            if (!selectedDepartment && !activeRouteDepartmentId && deptList.length > 0) {
                setSelectedDepartment(deptList[0]);
            } else if (activeRouteDepartmentId && !selectedDepartment && deptList.includes(activeRouteDepartmentId)) {
                setSelectedDepartment(activeRouteDepartmentId);
            }

        } catch (err) {
            console.error('Error loading departments:', err);
            setError(`Failed to load departments: ${err.message}`);
        }
    }, [neo4jService, selectedDepartment, activeRouteDepartmentId]);

    const loadDepartmentData = useCallback(async () => {
        if (!neo4jService || !selectedDepartment || !dateRange || !dateRange.start || !dateRange.end) {
            setLoading(false);
            return;
        }
        
        setLoading(true);
        setError(null);
        
        try {
            const queryParams = { 
                department: selectedDepartment, 
                startDate: dateRange.start, 
                endDate: dateRange.end,
                driFilter: selectedDris.length > 0 ? selectedDris : null
            };
            
            const requiredQueries = [
                'getDepartmentPriorityCounts',
                'getDepartmentStaffWorkload', 'getDepartmentClientHealth',
                'getDepartmentRecentPriorityCommunications'
            ];

            for (const queryName of requiredQueries) {
                if (!departmentQueries?.[queryName] || typeof departmentQueries[queryName] !== 'string' || !departmentQueries[queryName].trim()) {
                    console.error(`InternalOverviewView: Missing or invalid query: departmentQueries.${queryName}`);
                    setError(`Internal error: Query '${queryName}' not found or invalid. Check graphQueries.js`);
                    setLoading(false);
                    return;
                }
            }
            
            const [
                countsResult, workloadResult, healthResult, commsResult
            ] = await Promise.all([
                neo4jService.runQuery(departmentQueries.getDepartmentPriorityCounts, queryParams),
                neo4jService.runQuery(departmentQueries.getDepartmentStaffWorkload, queryParams),
                neo4jService.runQuery(departmentQueries.getDepartmentClientHealth, queryParams),
                neo4jService.runQuery(departmentQueries.getDepartmentRecentPriorityCommunications, queryParams)
            ]);
            
            if (countsResult && countsResult.length > 0) {
                const counts = countsResult[0];
                setPriorityCounts({
                    criticalIssues: counts.criticalIssuesCount || 0,
                    totalIssues: counts.totalIssuesCount || 0,
                    blockedInitiatives: counts.blockedInitiativesCount || 0,
                    totalInitiatives: counts.totalInitiativesCount || 0,
                    overdueActions: counts.overdueActionsCount || 0,
                    neglectedGoals: counts.neglectedGoalsCount || 0,
                    totalGoals: counts.totalGoalsCount || 0
                });
            } else {
                 setPriorityCounts({ criticalIssues: 0, totalIssues: 0, blockedInitiatives: 0, totalInitiatives: 0, overdueActions: 0, neglectedGoals: 0, totalGoals: 0 });
            }
            
            setStaffWorkload(workloadResult || []);
            setClientHealth(healthResult || []);
            setRecentComms(commsResult || []);
            
        } catch (err) {
            console.error(`Error loading data for department ${selectedDepartment}:`, err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [neo4jService, selectedDepartment, dateRange, selectedDris]);

    useEffect(() => {
        loadDepartments();
    }, [loadDepartments]);

    useEffect(() => {
        if (selectedDepartment && dateRange && dateRange.start && dateRange.end) {
            loadDepartmentData();
        }
    }, [selectedDepartment, dateRange, loadDepartmentData]);

    const handleDepartmentChange = (event) => {
        const newDept = event.target.value;
        navigate(`/internal/${newDept}/overview`);
    };
    
    const averageHealthScore = clientHealth.length > 0 
        ? Math.round(clientHealth.reduce((sum, c) => sum + (c.healthScore || 0), 0) / clientHealth.length)
        : 0;

    // Tooltip content for Client Health Score
    const clientHealthTooltipContent = (
        <div>
            <p className="font-medium mb-1">Client Health Score Calculation:</p>
            <ul className="list-disc pl-4 text-xs space-y-1">
                <li>Base score: 100 (or 50 if no active goals).</li>
                <li>Deductions:
                    <ul className="list-disc pl-4">
                        <li>-10 for each High Severity Issue.</li>
                        <li>-5 for each Blocked Initiative.</li>
                        <li>-2 for each Overdue Action.</li>
                    </ul>
                </li>
                <li>Score is clamped between 0 and 100.</li>
            </ul>
            <p className="mt-2 text-xs text-gray-500">The average score across all clients in the selected department is displayed.</p>
        </div>
    );

    if (!departments.length && !loading && !error) {
      return (
            <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 rounded">
                <p className="font-bold">No Departments Found</p>
                <p className="mt-2">Ensure staff nodes have a 'department' property in Neo4j.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
                <h2 className="text-2xl font-semibold">Internal Operations Dashboard</h2>
                <div className="flex items-center space-x-2">
                    <div className="flex items-center">
                        <DropdownCheckbox
                            options={dris}
                            selectedOptions={selectedDris}
                            onChange={handleDriChange}
                            title="Filter by DRI"
                        />
                        <Tooltip content={driTooltipContent}>
                            <InformationCircleIcon className="h-4 w-4 text-gray-500 ml-1 cursor-help" />
                        </Tooltip>
                    </div>
                    {departments.length > 0 && (
                        <>
                            <label htmlFor="department-select" className="text-sm font-medium text-gray-700">
                                Department:
                            </label>
                            <select
                                id="department-select"
                                value={selectedDepartment}
                                onChange={handleDepartmentChange}
                                className="block w-48 pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                                disabled={loading || !departments.length}
                            >
                                {departments.map(dept => (
                                    <option key={dept} value={dept}>{dept}</option>
                                ))}
                            </select>
                        </>
                    )}
                </div>
            </div>
            
            {error && <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded">Error: {error}</div>}

            {loading && (!departments.length || !selectedDepartment) && (
                 <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-800"></div>
                    <span className="ml-3">Loading departments...</span>
                </div>
            )}
            {loading && selectedDepartment && (
                 <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-800"></div>
                    <span className="ml-3">Loading data for {selectedDepartment}...</span>
                </div>
            )}

            {!loading && selectedDepartment && !error && (
                <>
                    {/* Priority Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                        <div className="bg-red-100 border border-red-200 rounded-lg shadow p-4">
                            <h3 className="text-sm font-medium text-red-700 mb-1">High Sev. Issues</h3>
                            <p className="text-3xl font-bold text-red-800">{priorityCounts.criticalIssues} <span className="text-lg text-red-600">/ {priorityCounts.totalIssues}</span></p>
                            <p className="text-xs text-red-600 mt-1">{selectedDepartment}</p>
                        </div>
                        <div className="bg-orange-100 border border-orange-200 rounded-lg shadow p-4">
                            <h3 className="text-sm font-medium text-orange-700 mb-1">Blocked Initiatives</h3>
                            <p className="text-3xl font-bold text-orange-800">{priorityCounts.blockedInitiatives} <span className="text-lg text-orange-600">/ {priorityCounts.totalInitiatives}</span></p>
                            <p className="text-xs text-orange-600 mt-1">{selectedDepartment}</p>
                        </div>
                        <div className="bg-yellow-100 border border-yellow-200 rounded-lg shadow p-4">
                            <h3 className="text-sm font-medium text-yellow-700 mb-1">Overdue Actions</h3>
                            <p className="text-3xl font-bold text-yellow-800">{priorityCounts.overdueActions}</p>
                            <p className="text-xs text-yellow-600 mt-1">{selectedDepartment}</p>
                        </div>
                        <div className="bg-purple-100 border border-purple-200 rounded-lg shadow p-4">
                            <h3 className="text-sm font-medium text-purple-700 mb-1">Active Goals</h3>
                            <p className="text-3xl font-bold text-purple-800">{priorityCounts.totalGoals}</p>
                            <p className="text-xs text-purple-600 mt-1">{selectedDepartment}</p>
                        </div>
                        <div className="bg-blue-100 border border-blue-200 rounded-lg shadow p-4">
                            <div className="flex items-center justify-between mb-1">
                                <h3 className="text-sm font-medium text-blue-700">Avg. Client Health</h3>
                                <Tooltip content={clientHealthTooltipContent}>
                                    <InformationCircleIcon className="h-4 w-4 text-blue-600" />
                                </Tooltip>
                            </div>
                            <p className="text-3xl font-bold text-blue-800">{averageHealthScore}%</p>
                            <p className="text-xs text-blue-600 mt-1">{clientHealth.length} clients in {selectedDepartment}</p>
                        </div>
                    </div>
                    
                    <div className="mt-6">
                         <EisenhowerMatrixView 
                            departmentId={selectedDepartment} 
                            clientId={null}
                            typeFilter={['Goal', 'Initiative', 'ActionItem', 'Issue']}
                            driFilter={selectedDris}
                        />
                    </div>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                        <div className="bg-white rounded-lg shadow p-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">{selectedDepartment} Staff Workload</h3>
                            <div className="overflow-x-auto">
                                <table className="min-w-full text-sm">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-3 py-2 text-left font-medium text-gray-500">Staff</th>
                                            <th className="px-3 py-2 text-center font-medium text-gray-500">Total Tasks</th>
                                            <th className="px-3 py-2 text-center font-medium text-gray-500">Overdue</th>
                                            <th className="px-3 py-2 text-center font-medium text-gray-500">Due Soon</th>
                                            <th className="px-3 py-2 text-center font-medium text-gray-500">Clients</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {staffWorkload.map((staff, index) => (
                                            <tr key={staff.staffName || index} className="hover:bg-gray-50">
                                                <td className="px-3 py-2 font-medium">{staff.staffName}</td>
                                                <td className="px-3 py-2 text-center">{staff.totalTasks}</td>
                                                <td className="px-3 py-2 text-center"><span className={`px-2 py-1 rounded text-xs font-medium ${staff.overdueTasks > 0 ? 'bg-red-100 text-red-800' : 'text-gray-500'}`}>{staff.overdueTasks}</span></td>
                                                <td className="px-3 py-2 text-center"><span className={`px-2 py-1 rounded text-xs font-medium ${staff.dueSoonTasks > 0 ? 'bg-yellow-100 text-yellow-800' : 'text-gray-500'}`}>{staff.dueSoonTasks}</span></td>
                                                <td className="px-3 py-2 text-center">{staff.clientCount}</td>
                                            </tr>
                                        ))}
                                        {staffWorkload.length === 0 && <tr><td colSpan="5" className="px-3 py-4 text-center text-gray-500">No staff workload data.</td></tr>}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div className="bg-white rounded-lg shadow p-6">
                             <h3 className="text-lg font-medium text-gray-900 mb-4">{selectedDepartment} Client Health (Top 10 by lowest score)</h3>
                            <div className="overflow-x-auto">
                                <table className="min-w-full text-sm">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-3 py-2 text-left font-medium text-gray-500">Client</th>
                                            <th className="px-3 py-2 text-center font-medium text-gray-500">Health</th>
                                            <th className="px-3 py-2 text-center font-medium text-gray-500">Goals</th>
                                            <th className="px-3 py-2 text-center font-medium text-gray-500">Issues</th>
                                            <th className="px-3 py-2 text-center font-medium text-gray-500">Blocked</th>
                                            <th className="px-3 py-2 text-center font-medium text-gray-500">Overdue</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {clientHealth.slice(0, 10).map((client, index) => (
                                            <tr key={client.clientId || index} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/clients/${client.clientId}/overview`)}>
                                                <td className="px-3 py-2 font-medium text-blue-600 hover:text-blue-800">{client.clientId}</td>
                                                <td className="px-3 py-2 text-center"><span className={`px-2 py-1 rounded text-xs font-medium ${client.healthScore >= 80 ? 'bg-green-100 text-green-800' : client.healthScore >= 60 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>{client.healthScore}%</span></td>
                                                <td className="px-3 py-2 text-center">{client.activeGoals}</td>
                                                <td className="px-3 py-2 text-center"><span className={client.highSeverityIssues > 0 ? 'text-red-600 font-medium' : ''}>{client.highSeverityIssues}</span></td>
                                                <td className="px-3 py-2 text-center"><span className={client.blockedInitiatives > 0 ? 'text-orange-600 font-medium' : ''}>{client.blockedInitiatives}</span></td>
                                                <td className="px-3 py-2 text-center"><span className={client.overdueActions > 0 ? 'text-yellow-600 font-medium' : ''}>{client.overdueActions}</span></td>
                                            </tr>
                                        ))}
                                        {clientHealth.length === 0 && <tr><td colSpan="6" className="px-3 py-4 text-center text-gray-500">No client health data.</td></tr>}
                                    </tbody>
                                </table>
                                {clientHealth.length > 10 && <p className="text-xs text-gray-500 mt-2 text-center">Showing 10 of {clientHealth.length} clients.</p>}
                            </div>
                        </div>
                    </div>

                     <div className="bg-white rounded-lg shadow p-6 mt-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Priority Communications - {selectedDepartment}</h3>
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                            {recentComms.map(comm => (
                                <div key={comm.id} className="border rounded-lg p-3 hover:bg-gray-50">
                                    <div className="flex justify-between items-start mb-2">
                                        <div>
                                            <span className="font-medium text-blue-600 hover:text-blue-800 cursor-pointer" onClick={() => navigate(`/clients/${comm.clientId}/sentiment`)}>{comm.clientId}</span>
                                            <span className="mx-2 text-gray-400">•</span>
                                            <span className="text-sm text-gray-600">{comm.type}</span>
                                            <span className="mx-2 text-gray-400">•</span>
                                            <span className="text-sm text-gray-600">{comm.staff}</span>
                                        </div>
                                        <span className="text-xs text-gray-500">{formatDateTime(comm.date)}</span>
                                    </div>
                                    <h4 className="font-medium mb-1">{comm.title || 'No Title'}</h4>
                                    <p className="text-sm text-gray-700 mb-2">{comm.summary}</p>
                                    <div className="flex gap-4 text-xs">
                                        {comm.highSeverityIssues > 0 && (<span className="text-red-600">High Severity Issues: {comm.highSeverityIssues}</span>)}
                                        {comm.blockedInitiatives > 0 && (<span className="text-orange-600">Blocked Initiatives: {comm.blockedInitiatives}</span>)}
                                    </div>
                                </div>
                            ))}
                            {recentComms.length === 0 && <p className="text-gray-500 text-sm">No recent priority communications found.</p>}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default InternalOverviewView;